import React, { useEffect, useRef, useState } from 'react';
import { StreamWithChatRate, GameFilter } from '../types';
import { Settings, TimeWindow, SwitchMode, StreamSource, AutoSwitchBehavior, SpamFilterSettings } from './Settings';
import { HeatSpark } from './HeatSpark';
import { TwitchPlayerController, VIDEO_READY } from '../services/twitchPlayer';
import { getHeatRatio, getHeatTier, isHeatReady, formatCompact, formatUptime } from '../utils/heat';
import { IconFlame, IconTimer, IconBan, IconParty, IconGlitch, IconPanel } from './icons';
import './StreamPlayer.css';

interface TwitchPlayer {
  play: () => void;
  setChannel: (channel: string) => void;
  setMuted: (muted: boolean) => void;
  getMuted: () => boolean;
  setVolume: (volume: number) => void;
  getVolume: () => number;
}

interface TwitchEmbed {
  getPlayer: () => TwitchPlayer;
  addEventListener: (event: string, callback: () => void) => void;
}

interface TwitchEmbedConstructor {
  new (elementId: string, options: {
    width: string | number;
    height: string | number;
    channel: string;
    parent: string[];
    layout?: 'video' | 'video-with-chat';
    autoplay?: boolean;
    muted?: boolean;
    allowfullscreen?: boolean;
  }): TwitchEmbed;
  /** Fired once the player accepts API commands */
  VIDEO_READY?: string;
}

declare global {
  interface Window {
    Twitch: {
      Embed: TwitchEmbedConstructor;
    };
  }
}

interface StreamPlayerProps {
  stream: StreamWithChatRate | null;
  /** The watched chat's last five minutes, per 5-second bucket */
  history?: number[];
  timeWindow: TimeWindow;
  switchMode: SwitchMode;
  threshold: number;
  languageFilter: string;
  cooldown: number;
  cooldownProgress: number;
  minChatRate: number;
  gameBlacklist: string[];
  streamBlacklist: string[];
  favoriteStream: string;
  streamSource: StreamSource;
  customStreams: string[];
  autoSwitchBehavior: AutoSwitchBehavior;
  spamFilter: SpamFilterSettings;
  gameFilter: GameFilter | null;
  notifyWhenHidden: boolean;
  isLoggedIn: boolean;
  sessionId: string | null;
  sidebarHidden: boolean;
  chatConnected?: boolean;
  /** Watch-party chat, shown under Twitch chat while a party is running */
  partyPanel?: React.ReactNode;
  /** The active channel's shared room — same slot, when no party is running */
  channelPanel?: React.ReactNode;
  /** Collapsed channel room takes only the height of its bar */
  channelPanelCollapsed?: boolean;
  partyActive?: boolean;
  onOpenParty?: () => void;
  onTimeWindowChange: (window: TimeWindow) => void;
  onSwitchModeChange: (mode: SwitchMode) => void;
  onThresholdChange: (threshold: number) => void;
  onLanguageFilterChange: (lang: string) => void;
  onCooldownChange: (cooldown: number) => void;
  onMinChatRateChange: (rate: number) => void;
  onGameBlacklistChange: (games: string[]) => void;
  onStreamBlacklistChange: (streams: string[]) => void;
  onFavoriteStreamChange: (stream: string) => void;
  onCustomStreamsChange: (streams: string[]) => void;
  onAutoSwitchBehaviorChange: (behavior: AutoSwitchBehavior) => void;
  onSpamFilterChange: (settings: SpamFilterSettings) => void;
  onNotifyWhenHiddenChange: (on: boolean) => void;
  onBlacklistCurrent: () => void;
  onToggleSidebar: () => void;
}

const STAGE_ID = 'twitch-embed-stage';

export const StreamPlayer: React.FC<StreamPlayerProps> = ({
  stream,
  history = [],
  timeWindow,
  switchMode,
  threshold,
  languageFilter,
  cooldown,
  cooldownProgress,
  minChatRate,
  gameBlacklist,
  streamBlacklist,
  favoriteStream,
  streamSource,
  customStreams,
  autoSwitchBehavior,
  spamFilter,
  gameFilter,
  notifyWhenHidden,
  isLoggedIn,
  sessionId,
  sidebarHidden,
  chatConnected = true,
  partyPanel,
  channelPanel,
  channelPanelCollapsed = false,
  partyActive,
  onOpenParty,
  onTimeWindowChange,
  onSwitchModeChange,
  onThresholdChange,
  onLanguageFilterChange,
  onCooldownChange,
  onMinChatRateChange,
  onGameBlacklistChange,
  onStreamBlacklistChange,
  onFavoriteStreamChange,
  onCustomStreamsChange,
  onAutoSwitchBehaviorChange,
  onSpamFilterChange,
  onNotifyWhenHiddenChange,
  onBlacklistCurrent,
  onToggleSidebar,
}) => {
  const playerRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<TwitchPlayerController | null>(null);
  const toggleMuteRef = useRef<() => void>(() => {});
  const [isMuted, setIsMuted] = useState(true);
  const mutedRef = useRef(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const login = stream ? stream.user_login.toLowerCase() : null;

  // 'M' hotkey mutes/unmutes the player (ignored while typing)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable
      ) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key.toLowerCase() === 'm') {
        e.preventDefault();
        toggleMuteRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Listen for fullscreen changes and track state
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Twitch chat rides in one iframe that is re-pointed on every jump
  useEffect(() => {
    const host = chatRef.current;
    if (!host) return;
    if (!login) {
      host.innerHTML = '';
      return;
    }
    let iframe = host.querySelector('iframe');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.width = '100%';
      iframe.height = '100%';
      iframe.frameBorder = '0';
      host.appendChild(iframe);
    }
    const src = `https://www.twitch.tv/embed/${login}/chat?parent=${window.location.hostname}&darkpopout`;
    if (iframe.src !== src) iframe.src = src;
  }, [login]);

  // One player for the whole session. The first stream boots the Twitch
  // embed; every jump after that re-tunes it in place (see
  // services/twitchPlayer), so a switch is near-instant and mute carries
  // over. Without the embed script, a bare player iframe is re-pointed.
  useEffect(() => {
    const host = playerRef.current;
    if (!login || !host) return;
    const parentDomain = window.location.hostname;

    if (window.Twitch?.Embed) {
      if (!controllerRef.current) {
        controllerRef.current = new TwitchPlayerController({
          initialMuted: mutedRef.current,
          create: (channel, muted) => {
            host.innerHTML = '';
            const stage = document.createElement('div');
            stage.id = STAGE_ID;
            stage.style.width = '100%';
            stage.style.height = '100%';
            host.appendChild(stage);
            return new window.Twitch.Embed(STAGE_ID, {
              width: '100%',
              height: '100%',
              channel,
              parent: [parentDomain],
              layout: 'video',
              autoplay: true,
              muted,
              allowfullscreen: true,
            });
          },
          readyEvent: window.Twitch.Embed.VIDEO_READY || VIDEO_READY,
        });
      }
      try {
        controllerRef.current.show(login);
        return;
      } catch (err) {
        console.error('Twitch embed failed, falling back to a plain player:', err);
        controllerRef.current = null;
      }
    }

    let iframe = host.querySelector<HTMLIFrameElement>('iframe.stage-fallback');
    if (!iframe) {
      host.innerHTML = '';
      iframe = document.createElement('iframe');
      iframe.className = 'stage-fallback';
      iframe.width = '100%';
      iframe.height = '100%';
      iframe.allowFullscreen = true;
      iframe.frameBorder = '0';
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
      host.appendChild(iframe);
    }
    const src = `https://player.twitch.tv/?channel=${login}&parent=${parentDomain}&autoplay=true&muted=${mutedRef.current}`;
    if (iframe.src !== src) iframe.src = src;
  }, [login]);

  useEffect(() => () => {
    controllerRef.current?.dispose();
    controllerRef.current = null;
  }, []);

  const toggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    mutedRef.current = next;

    if (controllerRef.current) {
      controllerRef.current.setMuted(next);
      return;
    }
    // A plain iframe has no public JavaScript API. Use its documented mute
    // parameter if the SDK could not load.
    const iframe = playerRef.current?.querySelector<HTMLIFrameElement>('iframe.stage-fallback');
    if (iframe) {
      const url = new URL(iframe.src);
      url.searchParams.set('muted', String(next));
      iframe.src = url.toString();
    }
  };
  toggleMuteRef.current = toggleMute;

  const heatReady = stream ? isHeatReady(stream) : false;
  const heat = stream && heatReady ? getHeatRatio(stream) : 0;
  const heatTier = heatReady ? getHeatTier(heat) : 'cold';
  const uptime = stream ? formatUptime(stream.started_at) : null;
  const chatRate = stream?.chatRate10s || 0;

  if (!stream) {
    return (
      <div className="stream-player-container">
        <div className="no-stream-selected">
          <div className="no-stream-content">
            <img src="/pogchamp.png" alt="" className="no-stream-emote" />
            <h1>pogchamp<span className="no-stream-tld">.tv</span></h1>
            <p>Auto-switch to the loudest chat on Twitch.</p>
            <div className="instructions">
              <p className="instructions-title">How it works</p>
              <p>Every top-100 chat is monitored live, streams are ranked by messages per second, and the player jumps wherever chat erupts.</p>
              <p>Pick any stream on the left to start watching.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stream-player-container">
      <div className="stream-header">
        <div className="stream-header-info">
          <div className="stream-name-row">
            <span className={`live-badge ${stream.type !== 'live' ? 'not-live' : ''}`}>
              {stream.type === 'live' ? 'Live' : stream.type === 'offline' ? 'Offline' : 'Channel'}
            </span>
            <h1>{stream.user_name}</h1>
            {stream.game_name && <span className="badge game">{stream.game_name}</span>}
            <span
              className={`heat-capsule heat-${heatTier} ${!heatReady ? 'calibrating' : ''}`}
              title={heatReady
                ? `${chatRate.toFixed(1)} msgs/s now · ${stream.chatRate5m?.toFixed(1) || '0.0'}/s 5-min average${heat >= 2 ? ` · chat is ${heat.toFixed(1)}× its usual pace` : ''} · the trace is the last five minutes`
                : 'Measuring chat baseline…'}
            >
              <HeatSpark history={history} tier={heatTier} ready={heatReady} />
              <span className="capsule-rate">{chatRate.toFixed(1)}/s</span>
              {heatReady && heatTier !== 'cold' && (
                <span className="capsule-mult">
                  <IconFlame size={10} />
                  {heat.toFixed(1)}×
                </span>
              )}
            </span>
            {!chatConnected && (
              <span className="chip warn" title="Chat monitor lost connection — reconnecting automatically">
                reconnecting…
              </span>
            )}
          </div>
          <p>{stream.title}</p>
        </div>
        <div className="stream-telemetry">
          {uptime && (
            <span className="tele-item" title={`Live for ${uptime}`}>
              <IconTimer size={11} />
              {uptime}
            </span>
          )}
          <span className="tele-item viewers" title={`${stream.viewer_count.toLocaleString()} watching`}>
            {formatCompact(stream.viewer_count)}
          </span>
        </div>
        <div className="stream-header-actions">
          {sidebarHidden && (
            <button
              className="mute-toggle"
              onClick={onToggleSidebar}
              title="Show sidebar"
            >
              <IconPanel size={17} />
            </button>
          )}
          <button
            className="mute-toggle"
            onClick={() => {
              const container = document.querySelector('.app');
              if (isFullscreen) {
                document.exitFullscreen();
              } else if (container) {
                (container as HTMLElement).requestFullscreen();
              }
            }}
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? (
              <svg width="17" height="17" viewBox="0 0 20 20" fill="currentColor">
                <path d="M3 3h5v2H5v3H3V3zm9 0h5v5h-2V5h-3V3zM3 12h2v3h3v2H3v-5zm14 0h2v5h-5v-2h3v-3z"/>
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 20 20" fill="currentColor">
                <path d="M3 3h5v2H6v2H4V3zm9 0h5v4h-2V5h-3V3zM4 11h2v2h2v2H3v-5h1zm11 0h2v4h-5v-2h3v-2z"/>
              </svg>
            )}
          </button>
          <button
            className="mute-toggle mute-btn"
            onClick={toggleMute}
            title={isMuted ? "Unmute" : "Mute"}
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? (
              <svg width="17" height="17" viewBox="0 0 20 20" fill="currentColor">
                <path d="M7.5 3.5L4 7H1v6h3l3.5 3.5v-13zM15 10l2.5-2.5-1-1L14 9l-2.5-2.5-1 1L13 10l-2.5 2.5 1 1L14 11l2.5 2.5 1-1L15 10z"/>
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 20 20" fill="currentColor">
                <path d="M7.5 3.5L4 7H1v6h3l3.5 3.5v-13zM14 10c0-1.5-1-3-2.5-3.5v7c1.5-.5 2.5-2 2.5-3.5zM11.5 2v1.5c2.5.5 4.5 3 4.5 6s-2 5.5-4.5 6V17c3.5-.5 6-4 6-7s-2.5-6.5-6-7z"/>
              </svg>
            )}
          </button>
          <a
            className="mute-toggle twitch-out"
            href={`https://www.twitch.tv/${stream.user_login}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open on Twitch"
          >
            <IconGlitch size={15} />
          </a>
          {onOpenParty && (
            <button
              className={`party-launch-btn ${partyActive ? 'active' : ''}`}
              onClick={onOpenParty}
              title={partyActive ? 'Watch party active' : 'Start or join a watch party'}
            >
              <IconParty size={13} />
              {partyActive ? 'Party' : 'Watch Party'}
            </button>
          )}
          <button
            className="blacklist-btn"
            onClick={onBlacklistCurrent}
            title="Blacklist this stream from auto-switch"
          >
            <IconBan size={13} />
            <span className="blacklist-label">Blacklist</span>
          </button>
          <Settings
            timeWindow={timeWindow}
            switchMode={switchMode}
            threshold={threshold}
            languageFilter={languageFilter}
            cooldown={cooldown}
            minChatRate={minChatRate}
            gameBlacklist={gameBlacklist}
            streamBlacklist={streamBlacklist}
            favoriteStream={favoriteStream}
            streamSource={streamSource}
            customStreams={customStreams}
            autoSwitchBehavior={autoSwitchBehavior}
            spamFilter={spamFilter}
            gameFilter={gameFilter}
            notifyWhenHidden={notifyWhenHidden}
            isLoggedIn={isLoggedIn}
            sessionId={sessionId}
            onTimeWindowChange={onTimeWindowChange}
            onSwitchModeChange={onSwitchModeChange}
            onThresholdChange={onThresholdChange}
            onLanguageFilterChange={onLanguageFilterChange}
            onCooldownChange={onCooldownChange}
            onMinChatRateChange={onMinChatRateChange}
            onGameBlacklistChange={onGameBlacklistChange}
            onStreamBlacklistChange={onStreamBlacklistChange}
            onFavoriteStreamChange={onFavoriteStreamChange}
            onCustomStreamsChange={onCustomStreamsChange}
            onAutoSwitchBehaviorChange={onAutoSwitchBehaviorChange}
            onSpamFilterChange={onSpamFilterChange}
            onNotifyWhenHiddenChange={onNotifyWhenHiddenChange}
          />
        </div>
      </div>
      <div
        className="cooldown-progress-container"
        title={cooldownProgress >= 100
          ? 'Auto-switch armed'
          : `Auto-switch recharging — ready in ${Math.ceil(cooldown - (cooldown * cooldownProgress / 100))}s`}
      >
        <div
          className={`cooldown-progress-bar ${cooldownProgress >= 100 ? 'ready' : ''}`}
          style={{ width: `${cooldownProgress}%` }}
        />
        {cooldownProgress < 100 && (
          <div className="cooldown-text">
            {`armed in ${Math.ceil(cooldown - (cooldown * cooldownProgress / 100))}s`}
          </div>
        )}
      </div>
      <div className={`stream-content ${partyPanel ? 'with-party' : ''}`}>
        <div className="stream-stage">
          <div className="stream-player" ref={playerRef}></div>
          {/* keyed by stream so the switcher blink replays on every jump */}
          <span className="stage-static" key={stream.id} aria-hidden />
        </div>
        {/* Twitch chat on top; the party (or the channel's shared room) below it,
            like a second deck in the same rack */}
        <div className={`stream-chat ${partyPanel || (channelPanel && !channelPanelCollapsed) ? 'split' : ''}`}>
          <div className="stream-chat-twitch" ref={chatRef}></div>
          {partyPanel ? (
            <div className="stream-chat-party">{partyPanel}</div>
          ) : channelPanel ? (
            <div className={`stream-chat-channel ${channelPanelCollapsed ? 'collapsed' : ''}`}>{channelPanel}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
